import React from "react";
import { Link } from "react-router-dom";
import { ARTICLES, formatPublishDate } from "../lib/articles";
import { ArrowUpRight, BookOpen, Calculator, Clock, Users } from "lucide-react";

const SUBJECT_ICON = {
  English: BookOpen,
  Math: Calculator,
  Parents: Users,
};

const isRecent = (iso) => {
  if (!iso) return false;
  const ms = Date.now() - new Date(iso).getTime();
  return ms >= 0 && ms < 45 * 24 * 60 * 60 * 1000; // 45 days
};

export default function Articles() {
  const latest = ARTICLES[0];
  const latestSlug = latest?.slug;

  return (
    <section
      id="articles"
      className="py-20 lg:py-28 bg-[#FAFAFA]"
      data-testid="articles-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-sm font-medium text-[#D97706] uppercase tracking-widest mb-3">
              PSLE Study Tips
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0F172A] leading-tight">
              Articles to help your
              <br />
              <span className="text-[#1E3A8A]">child study smarter.</span>
            </h2>
            <p className="mt-5 text-base text-[#475569] leading-relaxed">
              Practical Do&apos;s and Don&apos;ts for PSLE English and Math —
              plus parenting tips for the study years. A new article every month.
            </p>
          </div>
          {latest && (
            <div className="hidden lg:flex items-center gap-3 text-sm text-[#475569]">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#E2E8F0]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" />
                Latest · {formatPublishDate(latest.publishedAt)}
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {ARTICLES.map((a) => {
            const Icon = SUBJECT_ICON[a.subject] || BookOpen;
            // Always badge the newest article, plus any others within 45 days.
            const recent = a.slug === latestSlug || isRecent(a.publishedAt);
            return (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                className="group relative block bg-white rounded-3xl border border-[#E2E8F0] hover:border-[#1E3A8A]/40 hover:-translate-y-1 hover:shadow-lg transition-all overflow-hidden"
                data-testid={`article-card-${a.slug}`}
              >
                {recent && (
                  <span
                    className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 bg-[#D97706] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
                    data-testid={`article-new-badge-${a.slug}`}
                  >
                    New
                  </span>
                )}
                <div className="relative h-40 bg-[#FAFAFA] overflow-hidden flex items-center justify-center">
                  <img
                    src={a.hero}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-contain p-4 opacity-95 group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] px-3 py-1 rounded-full text-xs font-medium text-[#1E3A8A]">
                    <Icon className="w-3.5 h-3.5" />
                    {a.subject}
                  </div>
                </div>
                <div className="p-6 lg:p-7">
                  <h3 className="font-heading text-lg lg:text-xl font-semibold text-[#0F172A] leading-snug group-hover:text-[#1E3A8A] transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed line-clamp-3">
                    {a.summary}
                  </p>
                  <div className="mt-5 flex items-center justify-between text-xs text-[#475569]">
                    <span
                      className="inline-flex items-center gap-3"
                      data-testid={`article-meta-${a.slug}`}
                    >
                      <span>{formatPublishDate(a.publishedAt)}</span>
                      <span className="text-[#E2E8F0]">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {a.readTime}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#1E3A8A]">
                      Read
                      <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <p className="mt-10 text-sm text-[#475569] text-center">
          New article every month. Check back — or follow us on WhatsApp for
          updates.
        </p>
      </div>
    </section>
  );
}
